import { useContext, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import ivclass from '@invideoio/web-shared/Modules/IVClass';

import AppLayout from '@layouts/AppLayout';
import { BlogTypeDetailsType, BlogHomepageCardDetailsType } from 'src/types';

import { StateContext } from '@store/store';
import { fetchAllProfessions, fetchProfessionDetails } from '@lib/API';
import { generateBreadcrumbsFromUrl } from '@utils/common.utils';
import ItemsTray from '@components/items-tray';
import Breadcrumb from '@components/breadcrumbs';
import HeaderWithCTA from '@components/header-with-cta';
import PaginatedBlogList from '@components/paginated-blog-listing';
import ProfessionCard from '@components/professions/profession-card';
import ProfessionHeader from '@components/profession-header-section';
import BlogSEO from '@components/SEO/BlogSEO';
import SubHeading from '@components/sub-heading';
import BlogPostCard from '@components/blog-posts/blog-post-card';
import useBlogAppLoad from '@hooks/useBlogAppLoad';
import { GetServerSidePropsContext } from 'next';

interface professionType {
  professionDetails: BlogTypeDetailsType;
}

const Profession = ({ professionDetails }: professionType) => {
  const globalState = useContext(StateContext);
  const { platform, browser } = globalState;
  const router = useRouter();
  const { trendingPosts, slug, name } = professionDetails;

  const pathName = `/blog/profession/${router.query.profession}/`;

  const [otherProfessions, setOtherProfessions] = useState<
    BlogHomepageCardDetailsType[]
  >([]);

  useBlogAppLoad({ platform });

  useEffect(() => {
    async function fetchOtherProfessions() {
      const otherProfessions = await fetchAllProfessions({
        listTypeToSkip: slug,
      });
      setOtherProfessions(otherProfessions);
    }
    fetchOtherProfessions();
  }, [slug]);

  return (
    <AppLayout platform={platform}>
      <BlogSEO seo={professionDetails?.SEO} url={pathName} />
      <HeaderWithCTA />
      <ProfessionHeader browser={browser} {...professionDetails} />
      {trendingPosts && trendingPosts.length > 0 && (
        <div className={ivclass('iv-bg-grey-7 iv-overflow-x-hidden')}>
          <div className={ivclass('iv-container iv-mx-auto')}>
            <div className={ivclass('iv-grid iv-grid-cols-12 iv-col-span-12')}>
              <div className={ivclass('iv-col-start-1 iv-col-span-12')}>
                <div
                  className={ivclass(
                    'iv-px-[0px] lg:iv-px-[73px] md:iv-px-25 sm:iv-px-20',
                    'iv-mt-[72px] lg:iv-mt-[70px] md:iv-mt-[45px] sm:iv-mt-[43px] ',
                    'iv-mb-[82px] lg:iv-mb-80 md:iv-mb-50 sm:iv-mb-[31px]',
                  )}
                >
                  <div
                    className={ivclass(
                      'iv-pl-[56px] lg:iv-pl-[39px] md:iv-pl-[32px] sm:iv-pl-[26px]',
                      'iv-mb-25 lg:iv-mb-[28px] md:iv-mb-[11px] sm:iv-mb-[12px]',
                    )}
                  >
                    <Breadcrumb
                      breadcrumbItems={generateBreadcrumbsFromUrl(
                        pathName.replace(/\/?$/, ''),
                      ).filter(path => path.name !== 'profession')}
                    />
                  </div>
                  <div className={ivclass('iv-container', 'iv-mx-auto')}>
                    <div className={ivclass('xl:iv-overflow-x-hidden')}>
                      <div
                        className={ivclass(
                          'iv-grid iv-grid-cols-12 iv-col-span-12',
                        )}
                      >
                        <div className={ivclass('iv-col-span-12')}>
                          <div
                            className={ivclass(
                              'iv-flex iv-justify-between iv-items-baseline',
                              'iv-mb-[67px] md:iv-mb-[32px] sm:iv-mb-15',
                              'iv-text-body-2xl iv-font-semibold ',
                            )}
                          >
                            <div
                              className={ivclass(
                                'iv-text-heading-2xl md:iv-text-heading-m sm:iv-text-[36px]',
                                'sm:iv-leading-[42px]',
                                'iv-font-extrabold',
                                'iv-pl-[56px] lg:iv-pl-[35px] md:iv-pl-[32px] sm:iv-pl-25',
                                'sm:iv-max-w-[280px]',
                              )}
                            >
                              {'Top trending posts 🔥'}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className={ivclass('sm:iv-mt-[7px]')}>
                        {trendingPosts && (
                          <ItemsTray
                            platform={platform}
                            sliderWrapperCls={ivclass(
                              'lg:iv-mr-20',
                              'md:iv-mr-[12px]',
                              'sm:iv-mr-20',
                            )}
                          >
                            {trendingPosts?.map(post => (
                              <div
                                key={post.id}
                                className={ivclass(
                                  'iv-relative',
                                  'iv-mr-40 md:iv-mr-25',
                                )}
                              >
                                <BlogPostCard
                                  {...post}
                                  devicePlatform={platform}
                                  browser={browser}
                                  backgroundColorClass={'iv-bg-grey-0'}
                                />
                              </div>
                            ))}
                          </ItemsTray>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        className={ivclass(
          'iv-mb-100 lg:iv-mb-100 md:iv-mb-[80px] sm:iv-mb-50',
        )}
      >
        <div className={ivclass('iv-grid iv-grid-cols-12 iv-col-span-12')}>
          <div className={ivclass('iv-col-start-1 iv-col-span-12')}>
            <PaginatedBlogList
              listTypeKey={'profession'}
              listName={name.toLowerCase()}
              browserURL={pathName}
              trendingPosts={trendingPosts}
              slug={slug}
              browser={browser}
              devicePlatform={platform}
            />
          </div>
        </div>
      </div>
      <div
        className={ivclass(
          'iv-pt-[90px] md:iv-pt-[72px] sm:iv-pt-[38px]',
          'iv-pb-[125px]',
          'iv-bg-black-4',
          'iv-overflow-hidden',
        )}
      >
        <div className={ivclass('iv-grid iv-grid-cols-12 iv-col-span-12')}>
          <div
            className={ivclass(
              'xl:iv-col-start-2 iv-col-start-1 ',
              'iv-col-span-12',
            )}
          >
            <div
              className={ivclass(
                'iv-mb-[41px]',
                'iv-px-60 lg:iv-px-[44px] md:iv-px-[21px] sm:iv-px-[21px]',
              )}
            >
              <SubHeading title={'Other professions'} />
            </div>
            {otherProfessions.length > 0 && (
              <div className={ivclass('iv-mx-[45px]')}>
                <ItemsTray
                  platform={platform}
                  sliderWrapperCls={ivclass(
                    'lg:iv-mr-15',
                    'md:iv-mr-15',
                    'sm:iv-mr-15',
                  )}
                >
                  {otherProfessions?.map(profession => (
                    <div
                      key={profession.id}
                      className={ivclass(
                        'iv-relative',
                        'iv-mr-30 iv-w-[242px]',
                        'sm:iv-w-[181px]',
                      )}
                    >
                      <ProfessionCard
                        platform={platform}
                        browser={browser}
                        {...profession}
                      />
                    </div>
                  ))}
                </ItemsTray>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Profession;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { profession } = context.query;

  const lang = context.locale;
  if (typeof profession === 'string') {
    const professionDetails = await fetchProfessionDetails(profession, lang);
    return {
      props: { professionDetails },
    };
  }

  return {
    notFound: true,
  };
}
